import React, { useState } from "react";
import { Check, X } from "lucide-react";
import { friendliesService } from "../../services/friendliesApi";
import { LocationModePill, OpponentTierLine } from "./FriendlyPills";

// TP-Friendlies apply modal (design Frame 3). One tap + an optional line. Adopts
// the MessageAgentModal skeleton: a compact modal with a read-only auto-generated
// application line built from the applying club's OWN profile, an optional personal
// note, Send → Sent state.
//
// The CANONICAL application message is built SERVER-side (fr.build_application_
// message, returned as `auto_message` on apply, and pre-send as the viewer-scoped
// `apply_message_preview` on the card — which includes the applying club's league
// tier). We prefer that authoritative preview; the client-built fallback (name +
// the friendly's team category + date, no tier) only applies if the field is absent.
const buildPreview = (viewerClubName, request) => {
  const parts = [viewerClubName || "Your club"];
  if (request?.team_category_label) parts.push(request.team_category_label);
  const lead = parts.join(" · ");
  const phrase = request?.date?.phrase;
  return phrase ? `${lead}: interested in your friendly on ${phrase}` : `${lead}: interested in your friendly`;
};

const ApplyModal = ({ request, viewerClubName = "", onClose, onApplied }) => {
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(null); // { auto_message } once sent

  if (!request) return null;
  const clubName = request.club?.name || "this club";
  const preview = request.apply_message_preview || buildPreview(viewerClubName, request);

  const handleSend = async () => {
    setSending(true);
    setError(null);
    const res = await friendliesService.apply(request.id, { note });
    setSending(false);
    if (res.success) {
      setSent(res.data || {});
      if (onApplied) onApplied(request, res.data);
    } else {
      setError(res.error || "Could not send your application. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/45 p-0 sm:p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-xl p-6 max-h-[92vh] overflow-y-auto">
        {sent ? (
          <div className="flex flex-col items-center text-center gap-2 py-4">
            <span className="w-11 h-11 rounded-full bg-[#E7F6EF] flex items-center justify-center">
              <Check className="w-5 h-5 text-[#0E7B52]" />
            </span>
            <span className="text-[15px] font-bold text-[#0F1728]">Application sent to {clubName}</span>
            {/* The authoritative server-built message (with the applying club's tier). */}
            {sent.auto_message && (
              <div className="mt-1 w-full rounded-lg bg-[#F0F7FF] border border-[#D5E6FF] px-3 py-2 text-[12.5px] text-[#1B3B66]" data-testid="friendly-sent-automsg">
                {sent.auto_message}
              </div>
            )}
            <span className="text-[13px] text-[#66707F] max-w-sm">
              It's now next to their request. If they start a chat, it shows up in Chat and under My applications.
            </span>
            <button
              onClick={onClose}
              className="mt-3 px-4 py-2 border border-[#E0E6EE] rounded-lg text-[13px] font-semibold text-[#33475E] hover:bg-gray-50"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <h3 className="text-[16px] font-bold text-[#0F1728]">Apply to {clubName}</h3>
                <div className="flex items-center flex-wrap gap-2 mt-1.5">
                  <LocationModePill location={request.location} />
                  <OpponentTierLine display={request.tier?.display} />
                </div>
                <p className="text-[12px] text-[#66707F] mt-1">
                  {[request.team_category_label, request.date?.phrase].filter(Boolean).join(" · ")}
                </p>
              </div>
              <button onClick={onClose} className="p-1 text-[#98A2B3] hover:text-[#475467]" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Auto-filled application line */}
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[12px] font-semibold text-[#33475E]">Your application</span>
                <span className="inline-flex items-center rounded-full bg-[#EAF3FF] text-[#1866CC] text-[9.5px] font-bold uppercase tracking-[0.04em] px-2 py-0.5">
                  Auto-filled from your club profile
                </span>
              </div>
              <div className="rounded-lg bg-[#F0F7FF] border border-[#D5E6FF] px-3 py-2.5 text-[13px] text-[#1B3B66]" data-testid="friendly-automsg">
                {preview}
              </div>
            </div>

            <label className="block text-[12px] font-semibold text-[#33475E] mb-1.5">
              Add a personal line <span className="font-normal text-[#98A2B3]">· optional</span>
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 200))}
              placeholder="e.g. We could also make a Sunday work if easier."
              className="w-full min-h-[72px] bg-gray-50 border border-[#E0E6EE] rounded-lg p-3 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#2283FF]/20"
            />

            <p className="mt-3 text-[12px] text-[#66707F]">
              {clubName} sees your application next to their request. If they're interested, they start the chat. You'll
              find it under My applications.
            </p>

            {error && <p className="mt-3 text-[12px] text-red-500">{error}</p>}

            <div className="flex justify-end gap-2 border-t border-gray-100 mt-4 pt-4">
              <button
                onClick={onClose}
                className="px-4 py-2.5 border border-[#E0E6EE] rounded-lg text-[13px] font-semibold text-[#33475E] hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={sending}
                className="px-4 py-2.5 bg-[#2283FF] text-white rounded-lg text-[13px] font-semibold hover:bg-[#186FE0] disabled:opacity-50"
              >
                {sending ? "Sending…" : "Send application"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ApplyModal;
